import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import { isOnMainProductPage } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /** @type {AbortController | null} */
  #syncAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupQuantitySync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#syncAbortController?.abort();
    this.#syncAbortController = null;
  }

  #shouldSyncGlobally() {
    if (!isOnMainProductPage(this)) return false;
    const { quantityInput } = this.refs;
    return quantityInput && !quantityInput.dataset.cartLine;
  }

  #setupQuantitySync() {
    if (!this.#shouldSyncGlobally()) return;

    this.#syncAbortController = new AbortController();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleGlobalQuantityUpdate, {
      signal: this.#syncAbortController.signal,
    });
  }

  /** @param {QuantitySelectorUpdateEvent} event */
  #handleGlobalQuantityUpdate = (event) => {
    if (Number.isFinite(event.detail?.cartLine)) return;

    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target || this.contains(target)) return;

    const sourceProductId = /** @type {HTMLElement | null} */ (target.closest('product-form-component'))?.dataset?.productId;
    const myProductId = /** @type {HTMLElement | null} */ (this.closest('product-form-component'))?.dataset?.productId;
    if (!myProductId || sourceProductId !== myProductId) return;

    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    const newValue = event.detail?.quantity;
    if (newValue != null && Number.parseInt(quantityInput.value) !== newValue) {
      quantityInput.value = String(newValue);
    }
  };

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
